import React, { useState } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, Divider,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { signInWithPopup, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth, googleProvider, db } from '../../firebase';
import { useContext } from 'react';
import { UserContext } from '../../context/UserContext';
import { doc, setDoc } from 'firebase/firestore';

const SignupModal = ({ onClose, onSwitchToLogin }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const { setUser } = useContext(UserContext);

  const createUserDocument = async (user) => {
    // Create a user document in Firestore
    try {
      await setDoc(doc(db, "users", user.uid), {
        uid: user.uid,
        displayName: user.displayName || name,
        email: user.email,
        photoURL: user.photoURL || null,
        createdAt: new Date().toISOString(),
        lastLogin: new Date().toISOString()
      });
      console.log("User document created in Firestore");
    } catch (error) {
      console.error("Error creating user document: ", error);
    }
  };

  const handleGoogleSignUp = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      setUser(result.user);
      await createUserDocument(result.user);
      onClose();
    } catch (error) {
      console.error("Error during Google sign-up: ", error);
      if (error.code === 'auth/popup-closed-by-user') {
        // User closed the popup, no need to show error
        return;
      }
      setError('Failed to sign up with Google. Please try again.');
    }
  };

  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    
    // Validate form inputs
    if (!name || !email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    try {
      // Create user with email and password
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update the user's profile with the name
      await updateProfile(user, {
        displayName: name
      });
      
      // Create user document in Firestore
      await createUserDocument({
        ...user,
        displayName: name
      });
      
      setUser(user);
      onClose();
    } catch (error) {
      console.error("Error during email/password sign-up: ", error);
      
      // Handle specific errors
      switch (error.code) {
        case 'auth/email-already-in-use':
          setError('Email is already in use. Please use a different email or login.');
          break;
        case 'auth/invalid-email':
          setError('Please enter a valid email address');
          break;
        case 'auth/weak-password':
          setError('Password is too weak. Please use a stronger password.');
          break;
        default:
          setError('An error occurred. Please try again later.');
      }
    }
  };

  return (
    <Dialog 
      open={true} 
      onClose={onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h5" component="h2">
          Create Account
        </Typography>
        <IconButton 
          edge="end" 
          color="inherit" 
          onClick={onClose} 
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Sign up to save your retirement scenarios and access them anywhere
        </Typography>
        
        {/* Google Sign-up Button */}
        <Button 
          variant="outlined"
          fullWidth 
          onClick={handleGoogleSignUp}
          sx={{ 
            py: 1.5, 
            mb: 2,
            justifyContent: 'flex-start',
            gap: 2
          }}
        >
          <img 
            src="https://developers.google.com/identity/images/g-logo.png" 
            alt="Google" 
            style={{ width: 20, height: 20 }} 
          />
          <span>Sign up with Google</span>
        </Button>
        
        <Box sx={{ position: 'relative', my: 3 }}>
          <Divider />
          <Typography 
            variant="body2" 
            sx={{ 
              position: 'absolute',
              top: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: 'background.paper',
              px: 2
            }}
          >
            or
          </Typography>
        </Box>
        
        {/* Email/Password Form */}
        <form onSubmit={handleEmailSignUp}>
          {error && (
            <Typography 
              color="error" 
              variant="body2" 
              sx={{ mb: 2 }}
            >
              {error}
            </Typography>
          )}
          
          <TextField
            label="Full Name"
            type="text"
            fullWidth
            margin="normal"
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          
          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          
          <TextField
            label="Password"
            type="password"
            fullWidth
            margin="normal"
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            helperText="Password must be at least 6 characters"
          />
          
          <TextField
            label="Confirm Password"
            type="password"
            fullWidth
            margin="normal"
            variant="outlined"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
          
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2, py: 1.5 }}
          >
            Create Account
          </Button>
        </form>
      </DialogContent>
      
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Typography variant="body2">
          Already have an account?{' '}
          <Box 
            component="span"
            onClick={onSwitchToLogin}
            sx={{ 
              color: 'primary.main',
              cursor: 'pointer',
              fontWeight: 500,
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Log in
          </Box>
        </Typography>
      </DialogActions>
    </Dialog>
  );
};

export default SignupModal;
